import React, { useMemo, useState } from 'react';
import { sanitizeForDisplay } from '../../../utils/sanitizeHtml';
import { processWikiContentImages } from '../../../utils/wikiImageUrls';
import WikiImageGallery from './WikiImageGallery';
import WikiTableOfContents from './WikiTableOfContents';
import styles from '../WikiPage.module.css';

const WikiPageArticle = ({ content, imageGallery }) => {
  const [copyStatus, setCopyStatus] = useState('');
  const blocks = useMemo(
    () => {
      const doc = new DOMParser().parseFromString(processWikiContentImages(sanitizeForDisplay(content || '')), 'text/html');
      doc.querySelectorAll('h1,h2,h3,h4,h5,h6').forEach((heading, index) => { heading.id = `heading-${index}`; });
      doc.querySelectorAll('p,li,pre').forEach((element) => {
        if (element.querySelector('p,li,pre')) return;
        const text = element.textContent.trim();
        const match = text.match(/^(\/[a-z][a-z0-9 _[\]<>а-яё.-]*?)\s+[-–—]\s*/i);
        const command = match?.[1] || (element.tagName === 'PRE' ? text : null);
        if (!command) return;
        const button = doc.createElement('button');
        button.type = 'button';
        button.dataset.copyCommand = command;
        button.setAttribute('aria-label', `Копировать ${command}`);
        button.textContent = 'Копировать';
        element.dataset.commandRow = '';
        element.appendChild(button);
      });
      doc.querySelectorAll('table').forEach((table) => {
        const wrapper = doc.createElement('div');
        wrapper.dataset.tableScroll = '';
        wrapper.tabIndex = 0;
        wrapper.setAttribute('role', 'region');
        wrapper.setAttribute('aria-label', 'Таблица: прокрутите для просмотра всех столбцов');
        table.replaceWith(wrapper); wrapper.appendChild(table);
      });
      if (!imageGallery) return [{ html: doc.body.innerHTML }];
      const blocks = [];
      let fragment = doc.createElement('div');
      let images = [];
      const flushText = () => { if (fragment.innerHTML.trim()) blocks.push({ html: fragment.innerHTML }); fragment = doc.createElement('div'); };
      const flushImages = () => { if (images.length) blocks.push({ images }); images = []; };
      [...doc.body.childNodes].forEach((node) => {
        const image = node.nodeName === 'IMG' ? node : node.nodeName === 'P' && !node.textContent.trim() && node.querySelectorAll('img').length === 1 ? node.querySelector('img') : null;
        if (image?.getAttribute('src')) {
          flushText(); images.push({ src: image.getAttribute('src'), alt: image.getAttribute('alt') || '' });
        } else if (!node.textContent?.trim() && (node.nodeType === 3 || (node.nodeName === 'P' && !node.querySelector('img,iframe,video')))) {
          if (!images.length) fragment.appendChild(node.cloneNode(true));
        } else {
          flushImages(); fragment.appendChild(node.cloneNode(true));
        }
      });
      flushText(); flushImages();
      return blocks;
    },
    [content, imageGallery]
  );
  const copy = async (event) => {
    const button = event.target.closest('[data-copy-command]');
    if (!button || !event.currentTarget.contains(button)) return;
    try {
      await navigator.clipboard.writeText(button.dataset.copyCommand);
      button.textContent = 'Скопировано';
      setCopyStatus(`Скопировано: ${button.dataset.copyCommand}`);
    } catch (_) { setCopyStatus('Не удалось скопировать. Выделите команду и скопируйте её вручную.'); }
  };
  return (
    <>
    {copyStatus && <p role="status" style={{ padding: '12px 20px', color: '#b5e9c6', fontSize: 13 }}>{copyStatus}</p>}
    <article
      className={styles.pageContent}
      data-wiki-article
      onClick={copy}
    >
      {blocks.map((block, index) => block.images
        ? <WikiImageGallery key={`${content}-${index}`} images={block.images} />
        : <div key={index} dangerouslySetInnerHTML={{ __html: block.html }} />)}
    </article>
    </>
  );
};

const WikiPageContent = ({ content, articleOnly, imageGallery = false }) =>
  articleOnly ? (
    <WikiPageArticle content={content} imageGallery={imageGallery} />
  ) : (
    <div className={styles.contentWrapper}>
      <div className={styles.articleColumn}>
        <WikiPageArticle content={content} imageGallery={imageGallery} />
      </div>
      <WikiTableOfContents content={content} />
    </div>
  );

export default WikiPageContent;
